import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Tooltip } from '@progress/kendo-react-tooltip'
import type { NarrativeOcgSeverity, NarrativeOcgValidationHint } from '../types'
import {
  getIssueCount,
  hasOcgValidationFindings,
} from '../utils/issueDetails'
import { NarrativeOcgIssueDetailsDialog } from './NarrativeOcgIssueDetailsDialog'
import ALERT_CIRCLE from '@public/assets/action/ocg-banner-alert.svg'

const ruleTooltipStyle = {
  maxWidth: '420px',
  border: 'none',
  backgroundColor: '#000',
  color: '#fff',
  padding: '4px 8px',
  fontSize: '14px',
  fontWeight: 400,
}

const ValidationRuleText = ({ text }: { text: string }) => {
  const textRef = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  const updateTruncation = useCallback(() => {
    const element = textRef.current
    setIsTruncated(
      Boolean(element && element.scrollWidth > element.clientWidth + 1)
    )
  }, [])

  useEffect(() => {
    updateTruncation()
    window.addEventListener('resize', updateTruncation)
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateTruncation)

    if (textRef.current) resizeObserver?.observe(textRef.current)

    return () => {
      window.removeEventListener('resize', updateTruncation)
      resizeObserver?.disconnect()
    }
  }, [text, updateTruncation])

  return (
    <div className="narrative-ocg-validation-bar__rule-container">
      <Tooltip
        anchorElement="target"
        parentTitle
        position="top"
        tooltipStyle={ruleTooltipStyle}
      >
        <span
          ref={textRef}
          className="narrative-ocg-validation-bar__rule"
          onMouseEnter={updateTruncation}
          title={isTruncated ? text : undefined}
        >
          {text}
        </span>
      </Tooltip>
    </div>
  )
}

const getFirstRuleName = (
  validationHint: NarrativeOcgValidationHint | undefined,
  severity: NarrativeOcgSeverity
) => {
  const finding = validationHint?.findings?.find(
    item => (item.severity ?? validationHint.severity) === severity
  )
  if (finding) return finding.rule_name || finding.ruleName || finding.message

  const rule = validationHint?.ruleHits?.find(
    item => (item.severity ?? validationHint?.severity) === severity
  )
  return rule?.rule_name || rule?.ruleName || rule?.message || ''
}

export const NarrativeOcgValidationBars = ({
  validationHint,
}: {
  validationHint?: NarrativeOcgValidationHint
}) => {
  const [activeSeverity, setActiveSeverity] = useState<
    NarrativeOcgSeverity | undefined
  >()

  if (
    !validationHint?.visible ||
    validationHint.isLoading ||
    !hasOcgValidationFindings(validationHint)
  )
    return null

  const severities: NarrativeOcgSeverity[] = ['error', 'warning']

  return (
    <>
      <div className="narrative-ocg-validation-bars">
        {severities.map(severity => {
          const count = getIssueCount(validationHint, severity)
          if (!count) return null
          const label = count === 1 ? severity : `${severity}s`
          const ruleName = getFirstRuleName(validationHint, severity) || ''

          return (
            <div
              className={`narrative-ocg-validation-bar narrative-ocg-validation-bar--${severity}`}
              key={severity}
            >
              <span className="narrative-ocg-validation-bar__icon">
                <img alt="" src={ALERT_CIRCLE} />
              </span>
              <strong>
                {count} {label[0].toUpperCase() + label.slice(1)}
              </strong>
              <span className="narrative-ocg-validation-bar__divider" />
              <ValidationRuleText text={ruleName} />
              <button
                className="narrative-ocg-validation-bar__view"
                onClick={() => setActiveSeverity(severity)}
                type="button"
              >
                View All
              </button>
            </div>
          )
        })}
      </div>
      {activeSeverity ? (
        <NarrativeOcgIssueDetailsDialog
          validationHint={validationHint}
          severity={activeSeverity}
          onClose={() => setActiveSeverity(undefined)}
        />
      ) : null}
    </>
  )
}
